let ctx = null;
const getCtx = () => {
  if (!ctx) ctx = new (window.AudioContext || window.webkitAudioContext)();
  if (ctx.state === 'suspended') ctx.resume();
  return ctx;
};

let volMult = 1;

const note = (ac, freq, start, dur, vol = 0.18, type = 'sine') => {
  const osc = ac.createOscillator();
  const gain = ac.createGain();
  osc.type = type;
  osc.frequency.value = freq;
  gain.gain.setValueAtTime(0, ac.currentTime + start);
  gain.gain.linearRampToValueAtTime(vol * volMult, ac.currentTime + start + 0.04);
  gain.gain.exponentialRampToValueAtTime(0.0001, ac.currentTime + start + dur);
  osc.connect(gain).connect(ac.destination);
  osc.start(ac.currentTime + start);
  osc.stop(ac.currentTime + start + dur + 0.05);
};

// ---- Volume preferences (0-100, default 100 = original loudness) ----
const VOL_KEYS = { tone: 'jmepl_vol_tone', greeting: 'jmepl_vol_greeting', notification: 'jmepl_vol_notification' };
export const getVolume = (kind) => {
  const raw = localStorage.getItem(VOL_KEYS[kind]);
  if (raw === null) return 100;
  const v = parseInt(raw, 10);
  return Number.isNaN(v) ? 100 : Math.min(100, Math.max(0, v));
};
export const setVolume = (kind, v) => localStorage.setItem(VOL_KEYS[kind], String(v));

export const TONES = [
  {
    id: 'gentle_chime',
    name: 'Gentle Chime',
    description: 'Two soft rising notes',
    duration: 2100,
    play: () => {
      const ac = getCtx();
      note(ac, 1046.5, 0, 1.4, 0.16);
      note(ac, 1568.0, 0.18, 1.8, 0.13);
    },
  },
  {
    id: 'soft_bell',
    name: 'Soft Bell',
    description: 'A single calm bell with a long fade',
    duration: 2500,
    play: () => {
      const ac = getCtx();
      note(ac, 1318.5, 0, 2.4, 0.16);
      note(ac, 2637.0, 0, 1.6, 0.05);
      note(ac, 659.25, 0, 2.4, 0.06);
    },
  },
  {
    id: 'calm_melody',
    name: 'Calm Melody',
    description: 'A gentle 4-note welcome tune',
    duration: 2400,
    play: () => {
      const ac = getCtx();
      note(ac, 523.25, 0, 0.9, 0.14, 'triangle');
      note(ac, 659.25, 0.22, 0.9, 0.14, 'triangle');
      note(ac, 783.99, 0.44, 0.9, 0.14, 'triangle');
      note(ac, 1046.5, 0.66, 1.6, 0.15, 'triangle');
    },
  },
  {
    id: 'morning_dew',
    name: 'Morning Dew',
    description: 'Soft descending sparkle',
    duration: 2500,
    play: () => {
      const ac = getCtx();
      note(ac, 1567.98, 0, 1.0, 0.11);
      note(ac, 1318.51, 0.25, 1.1, 0.12);
      note(ac, 1046.5, 0.5, 1.9, 0.14);
    },
  },
];

export const playNotificationAlert = () => {
  volMult = getVolume('notification') / 100;
  if (!volMult) { volMult = 1; return; }
  const ac = getCtx();
  // Soothing two-note bell with a soft sparkle overtone
  note(ac, 880, 0, 1.3, 0.14);
  note(ac, 1174.66, 0.35, 1.7, 0.12);
  note(ac, 1760, 0.35, 1.0, 0.04);
  note(ac, 587.33, 0.35, 1.7, 0.05);
  volMult = 1;
};

const KEY = 'jmepl_welcome_tone';
const GREET_KEY = 'jmepl_voice_greeting';

// ---- Welcome sequence gate: notification sounds must wait until tone + greeting finish ----
let sequenceDone = !!sessionStorage.getItem('jmepl_welcome_played');
const doneCallbacks = [];
export const isWelcomeSequenceDone = () => sequenceDone;
export const onWelcomeSequenceDone = (cb) => { if (sequenceDone) cb(); else doneCallbacks.push(cb); };
export const markWelcomeSequenceDone = () => {
  if (sequenceDone) return;
  sequenceDone = true;
  doneCallbacks.splice(0).forEach((cb) => { try { cb(); } catch (e) { console.error('Welcome sequence callback failed:', e); } });
};
export const resetWelcomeSequence = () => { sequenceDone = false; };

// Major Indian festivals (Hindu calendar + national days), 2025–2027
export const FESTIVALS = {
  '2025-10-02': 'Dussehra', '2025-10-20': 'Diwali', '2025-12-25': 'Christmas',
  '2026-01-01': 'New Year', '2026-01-14': 'Makar Sankranti', '2026-01-26': 'Republic Day',
  '2026-02-15': 'Maha Shivratri', '2026-03-03': 'Holika Dahan', '2026-03-04': 'Holi',
  '2026-03-26': 'Ram Navami', '2026-08-15': 'Independence Day', '2026-08-28': 'Raksha Bandhan',
  '2026-09-04': 'Janmashtami', '2026-09-14': 'Ganesh Chaturthi', '2026-10-11': 'Navratri',
  '2026-10-20': 'Dussehra', '2026-11-08': 'Diwali', '2026-11-09': 'Govardhan Puja',
  '2026-11-10': 'Bhai Dooj', '2026-12-25': 'Christmas',
  '2027-01-01': 'New Year', '2027-01-14': 'Makar Sankranti', '2027-01-26': 'Republic Day',
  '2027-08-15': 'Independence Day', '2027-12-25': 'Christmas',
};

export const getTodayFestival = () => {
  const d = new Date();
  const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return FESTIVALS[key] || null;
};

export const isGreetingEnabled = () => localStorage.getItem(GREET_KEY) !== 'off';
export const setGreetingEnabled = (on) => localStorage.setItem(GREET_KEY, on ? 'on' : 'off');

const GREET_TEXT_KEY = 'jmepl_greeting_text';
export const getCustomGreeting = () => localStorage.getItem(GREET_TEXT_KEY) || '';
export const setCustomGreeting = (t) => {
  if (t && t.trim()) localStorage.setItem(GREET_TEXT_KEY, t.trim());
  else localStorage.removeItem(GREET_TEXT_KEY);
};

const VOICE_KEY = 'jmepl_ai_voice';

export const AI_VOICES = [
  { id: 'coral', name: 'Coral', tag: 'Female · Warm & Friendly' },
  { id: 'shimmer', name: 'Shimmer', tag: 'Female · Bright & Cheerful' },
  { id: 'nova', name: 'Nova', tag: 'Female · Energetic & Upbeat' },
  { id: 'sage', name: 'Sage', tag: 'Female · Calm & Soothing' },
  { id: 'onyx', name: 'Onyx', tag: 'Male · Deep & Rich' },
  { id: 'echo', name: 'Echo', tag: 'Male · Smooth' },
];

const FEMALE_DEFAULT = 'coral';
const MALE_VOICES = ['onyx', 'echo', 'ash', 'fable'];

export const getSelectedVoice = () => {
  const v = localStorage.getItem(VOICE_KEY);
  if (!v) return FEMALE_DEFAULT;
  // One-time migration: switch previously stored male voices to the female default
  if (MALE_VOICES.includes(v) && !localStorage.getItem('jmepl_voice_migrated_female')) {
    localStorage.setItem('jmepl_voice_migrated_female', '1');
    localStorage.setItem(VOICE_KEY, FEMALE_DEFAULT);
    return FEMALE_DEFAULT;
  }
  return v;
};
export const setSelectedVoice = (id) => {
  localStorage.setItem('jmepl_voice_migrated_female', '1');
  localStorage.setItem(VOICE_KEY, id);
};

const pickVoice = () => {
  const voices = window.speechSynthesis?.getVoices() || [];
  const female = /female|woman|heera|veena|kalpana|zira|samantha|swara/i;
  return (
    voices.find(v => /en[-_]IN/i.test(v.lang) && female.test(v.name)) ||
    voices.find(v => /^en/i.test(v.lang) && female.test(v.name)) ||
    voices.find(v => /en[-_]IN/i.test(v.lang) && /google/i.test(v.name)) ||
    voices.find(v => /en[-_]IN/i.test(v.lang)) ||
    voices.find(v => /google/i.test(v.name) && /^en/i.test(v.lang)) ||
    voices.find(v => /^en/i.test(v.lang)) || null
  );
};

export const buildGreetingText = (name) => {
  const festival = getTodayFestival();
  const custom = getCustomGreeting();
  let text;
  if (custom) {
    text = custom.includes('{name}') ? custom.split('{name}').join(name || 'there') : custom;
  } else {
    text = `Hey, welcome back ${name || 'there'}! Hope you had a great day today.`;
  }
  if (festival) text += ` And wishing you and your family a very happy ${festival}!`;
  return text;
};

const fetchGreetingUrl = async (text, voice) => {
  const res = await fetch(
    `${process.env.REACT_APP_BACKEND_URL}/api/greeting/audio?text=${encodeURIComponent(text)}&voice=${voice}`,
    { credentials: 'include' }
  );
  if (!res.ok) throw new Error('tts failed');
  return URL.createObjectURL(await res.blob());
};

const playAudioUrl = (url, gv) => new Promise((resolve) => {
  const audio = new Audio(url);
  audio.volume = Math.min(1, 0.95 * gv);
  audio.onended = () => { URL.revokeObjectURL(url); resolve(); };
  audio.onerror = () => { URL.revokeObjectURL(url); resolve(); };
  audio.play().catch(() => resolve());
});

// Start fetching TTS audio NOW (in parallel with the welcome tone) so the greeting can play instantly
export const prefetchGreeting = (name, voiceOverride) =>
  fetchGreetingUrl(buildGreetingText(name), voiceOverride || getSelectedVoice()).catch(() => null);

export const playPrefetchedGreeting = async (urlPromise, name) => {
  const gv = getVolume('greeting') / 100;
  if (!gv) return;
  const url = await urlPromise;
  if (url) await playAudioUrl(url, gv);
  else await browserSpeak(buildGreetingText(name));
};

export const speakGreeting = async (name, voiceOverride) => {
  const gv = getVolume('greeting') / 100;
  if (!gv) return;
  try {
    const url = await fetchGreetingUrl(buildGreetingText(name), voiceOverride || getSelectedVoice());
    await playAudioUrl(url, gv);
  } catch {
    await browserSpeak(buildGreetingText(name));
  }
};

const browserSpeak = (text) => new Promise((resolve) => {
  if (!window.speechSynthesis) { resolve(); return; }
  const speak = () => {
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    const v = pickVoice();
    if (v) u.voice = v;
    u.rate = 0.95;
    u.pitch = 1.05;
    u.volume = Math.min(1, 0.9 * (getVolume('greeting') / 100));
    u.onend = resolve;
    u.onerror = resolve;
    window.speechSynthesis.speak(u);
  };
  if (window.speechSynthesis.getVoices().length === 0) {
    window.speechSynthesis.onvoiceschanged = () => { window.speechSynthesis.onvoiceschanged = null; speak(); };
    window.speechSynthesis.getVoices();
    setTimeout(speak, 400);
  } else {
    speak();
  }
});

export const getSelectedTone = () => localStorage.getItem(KEY) || 'gentle_chime';
export const setSelectedTone = (id) => localStorage.setItem(KEY, id);

export const playTone = (id) => {
  if (id === 'off') return;
  volMult = getVolume('tone') / 100;
  if (!volMult) { volMult = 1; return; }
  const t = TONES.find(x => x.id === id);
  try { t?.play(); } catch (e) { console.debug('Tone playback unavailable:', e?.message); }
  volMult = 1;
};

export const playGreetingSample = (name) => {
  const toneId = getSelectedTone();
  const tone = TONES.find(t => t.id === toneId);
  playTone(toneId);
  setTimeout(() => { if (isGreetingEnabled()) speakGreeting(name); }, toneId === 'off' ? 0 : (tone?.duration || 2000));
};

export const playWelcomeTone = (userName, onComplete) => {
  if (sessionStorage.getItem('jmepl_welcome_played')) { markWelcomeSequenceDone(); return; }
  const toneId = getSelectedTone();
  const greet = isGreetingEnabled();
  if (toneId === 'off' && !greet) {
    sessionStorage.setItem('jmepl_welcome_played', '1');
    markWelcomeSequenceDone();
    return;
  }
  const attempt = () => {
    try {
      sessionStorage.setItem('jmepl_welcome_played', '1');
      const tone = TONES.find(t => t.id === toneId);
      const toneMs = toneId === 'off' ? 0 : (tone?.duration || 2000);
      const greetPromise = greet && getVolume('greeting') > 0 ? prefetchGreeting(userName) : null;
      playTone(toneId);
      setTimeout(async () => {
        if (greetPromise) await playPrefetchedGreeting(greetPromise, userName);
        if (onComplete) await onComplete();
        markWelcomeSequenceDone();
      }, toneMs);
      return true;
    } catch { markWelcomeSequenceDone(); return false; }
  };
  const ac = new (window.AudioContext || window.webkitAudioContext)();
  if (ac.state === 'running') {
    ac.close();
    attempt();
  } else {
    ac.close();
    const onFirstClick = () => { attempt(); document.removeEventListener('click', onFirstClick); };
    document.addEventListener('click', onFirstClick, { once: true });
  }
};
